import { Component, computed, DestroyRef, inject, OnInit, signal, Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { DividerComponent, InstructionsComponent } from '@r1-compass-library/components';

import {
    deprecatedsalesforceUniqueRecommendationFieldTargetNamePlaceholderValue,
    FileMappingRow,
    PlayConfig,
    SalesforceField,
    SalesforceFieldMetadata,
    SalesforceObjectMetadata,
} from '@r1-compass-library/models';
import { FileMappingRowForm, PlayFileImportForm } from '@r1-compass-library/models/play/play-file-import-form.model';
import { SalesforceService, SnackBarService } from '@r1-compass-library/services';
import { AssetStateMessageHandlingService } from '@r1-compass-library/services/asset-state-message-handling.service';
import { FileTransferService } from '@r1-compass-library/services/file-transfer.service';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { NgxDropzoneChangeEvent, NgxDropzoneModule } from 'ngx-dropzone';
import { filterNil } from 'ngxtension/filter-nil';
import { injectParams } from 'ngxtension/inject-params';
import { PlayStore } from '../../store/play.store';
import { PlayRecordFormService } from '../play-record-form.service';
import { FileDropzoneComponent } from './file-dropzone/file-dropzone.component';
import { FileMappingRowComponent } from './file-mapping-row/file-mapping-row.component';
import { FilePreviewComponent } from './file-preview/file-preview.component';
import { exhaustMap, firstValueFrom, of, switchMap } from 'rxjs';
import { noopFormSaveMutationResult } from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { PlayRecordChildFormWarning } from '@r1-compass-library/guards/save-guards/play-record-selection.guard';
import { ErrorHandlingService } from '@r1-compass-library/services/error-handling.service';
import { PlayImporterFormService } from './play-importer-form.service';
import { PlayValidationStateService } from '../../play-validation-state.service';

@Component({
    selector: 'r1-compass-app-play-record-play-importer',
    imports: [
        DividerComponent,
        FileDropzoneComponent,
        FileMappingRowComponent,
        FilePreviewComponent,
        InstructionsComponent,
        MatButtonModule,
        MatInputModule,
        MatSelectModule,
        NgxDropzoneModule,
        ReactiveFormsModule,
    ],
    templateUrl: './play-record-play-importer.component.html',
    styleUrl: './play-record-play-importer.component.scss',
})
export class PlayRecordPlayImporterComponent implements OnInit, PlayRecordChildFormWarning {
    readonly #playStore = inject(PlayStore);
    readonly #fileTransferService = inject(FileTransferService);
    readonly #salesforceService = inject(SalesforceService);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #playRecordFormService = inject(PlayRecordFormService);
    readonly #assetStateMessageHandlingService = inject(AssetStateMessageHandlingService);
    readonly #formSaveWarningStore = inject(SaveGuardStore);
    readonly #snackBarService = inject(SnackBarService);
    readonly #destroyRef$ = inject(DestroyRef);
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #playImporterFormService = inject(PlayImporterFormService);
    readonly #playValidationStateService = inject(PlayValidationStateService);

    private selectedDivisionConfig$ = toObservable(this.#globalLoggedInUserStore.selectedDivisionConfig);

    playObjectName = '';
    recommendationObjectName = '';
    csvHeaders: string[] = [];
    playFields: SalesforceField[] = [];
    recommendationFields = signal<SalesforceField[]>([]);
    uploadedFilePreview: string[][] | undefined;
    file: File | undefined;

    playId = injectParams('id');
    play = toSignal(this.#playStore.play$.pipe(filterNil()));
    divisionConfig = toSignal(this.selectedDivisionConfig$.pipe(filterNil()));
    loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;

    playImporterForm: FormGroup<PlayFileImportForm> = this.#playRecordFormService.playImporterForm;

    canDeactivate$ = this.#formSaveWarningStore.deriveCanDeactivate$(this.playImporterForm);

    private readonly canDeactivate = toSignal(this.canDeactivate$, { requireSync: true });
    // See `shouldDisableSave`'s service for context
    private doNotBlock = signal(false).asReadonly();

    public disableSave = computed<boolean>(() =>
        this.#playValidationStateService.shouldDisableSave(
            this.play(),
            this.canDeactivate(),
            this.#playValidationStateService.invalidSections(),
            'recordSelection',
            this.doNotBlock()
        )
    );

    private maxPreviewLength = 256;

    protected salesforceUniqueRecommendationFieldTargetOption = computed<SalesforceObjectMetadata>(() => {
        const uniqueField = this.salesforceUniqueRecommendationField();
        return {
            name: uniqueField?.name ?? '',
            label: uniqueField?.label ?? '',
        };
    });

    ngOnInit(): void {
        this.subscribeToStores();
        const playId = this.playId();
        if (playId && playId !== 'new' && this.#playStore.play()?.id !== +playId) {
            this.#playStore.getPlay({ id: +playId });
        }
        this.#playStore.message$.pipe(filterNil()).subscribe(message => {
            this.#assetStateMessageHandlingService.handleMessage(message);
            this.#playStore.resetMessage();
        });
    }

    private subscribeToStores() {
        this.selectedDivisionConfig$.pipe(filterNil()).subscribe(divisionConfig => {
            const playObjectName = divisionConfig.config?.mapping?.salesforcePlayObject;
            if (playObjectName) {
                this.playObjectName = playObjectName;
                this.#salesforceService.getFieldsForObject(playObjectName).subscribe(res => {
                    this.playFields = this.convertToSalesforceFields(res.fields, playObjectName);
                });
            }
            const recommendationObjectName = divisionConfig.config?.mapping?.salesforceRecommendationObject;
            if (recommendationObjectName) {
                this.recommendationObjectName = recommendationObjectName;
                this.#salesforceService.getFieldsForObject(recommendationObjectName).subscribe(res => {
                    this.recommendationFields.set(this.convertToSalesforceFields(res.fields, recommendationObjectName));
                });
            }
            const play = this.play();
            if (play) {
                const trimmedHeaders = (play.config?.playFileImport?.headers ?? []).map(h => h.trim());
                this.csvHeaders = trimmedHeaders;
                const fileName = play.config?.playFileImport?.fileName;
                if (fileName) {
                    this.file = new File(new Array<Blob>(), play.config?.playFileImport?.fileName ?? '');
                }
                this.#playImporterFormService.populatePlayImporterForm(
                    this.playImporterForm,
                    play.config?.playFileImport,
                    playObjectName,
                    recommendationObjectName
                );
                if (this.playImporterForm.controls.customFileMapping.length === 0) {
                    this.playImporterForm.controls.customFileMapping.push(
                        new FormGroup(new FileMappingRowForm(undefined))
                    );

                    this.playImporterForm.controls.customFileMapping.controls
                        .at(0)
                        ?.controls.recommendationFieldName.patchValue(
                            deprecatedsalesforceUniqueRecommendationFieldTargetNamePlaceholderValue
                        );
                }

                //once the play has been active and has run for the first time a user cannot change the unique values
                if (play.lastSuccessfulRun) {
                    this.playImporterForm.controls.customFileMapping.controls
                        .find(mapping => mapping.controls.mappingId.value === 'unique-mapping')
                        ?.disable();
                }
            }
        });
    }

    onDownload() {
        if (this.file) {
            this.#fileTransferService.getFile(this.file.name, String(this.play()?.id)).subscribe(response => {
                const fileData = new Blob([response], { type: 'text/csv' });
                const data = window.URL.createObjectURL(fileData);
                const link = document.createElement('a');
                link.href = data;
                link.download = `${this.file!.name}`;
                link.click();
                window.URL.revokeObjectURL(data);
                link.remove();
            });
        }
    }

    onFileAdded(event: NgxDropzoneChangeEvent) {
        const fileToUpload = event.addedFiles[0];
        const playId = this.playId();
        if (fileToUpload && playId) {
            this.#fileTransferService
                .getSignedUrl(fileToUpload.name, playId)
                .pipe(
                    exhaustMap(response => this.uploadFile(response.url, response.contentType, fileToUpload, playId)),
                    takeUntilDestroyed(this.#destroyRef$)
                )
                .subscribe();
        }
    }

    private uploadFile(presignedUrl: string, contentType: string, fileToUpload: File, playId: string) {
        return this.#fileTransferService
            .uploadFile(fileToUpload, presignedUrl, contentType)
            .pipe(switchMap(() => this.validateFile(playId, fileToUpload)));
    }

    private validateFile(playId: string, playImporterFile: File) {
        const play = this.play();
        let fileUniqueColumn: string | undefined;
        if (play?.lastSuccessfulRun) {
            fileUniqueColumn = this.playImporterForm.controls.salesforceUniqueFieldSourceName.value;
        }
        return this.#fileTransferService.validateFile(playImporterFile.name, playId, fileUniqueColumn).pipe(
            switchMap(res => {
                if (res.valid) {
                    return this.getFilePreview(playId, playImporterFile);
                } else {
                    const message = res.errorMessage ?? 'An unknown error occurred';
                    this.#snackBarService.openGenericSnackbar({
                        messages: ['CSV Validation error: ' + message],
                        verticalPosition: 'top',
                        panelClass: 'generic-snack-bar-warning',
                        showDismissButton: true,
                    });
                    return of<void>();
                }
            })
        );
    }

    private getFilePreview(playId: string, playImporterFile: File) {
        return this.#fileTransferService.getFilePreview(playImporterFile.name, playId).pipe(
            switchMap(preview => {
                preview = this.truncatePreviewData(preview);
                this.file = playImporterFile;
                this.playImporterForm.controls.fileName.setValue(playImporterFile.name);
                this.csvHeaders = preview[0]?.map(header => header.trim());
                this.playImporterForm.controls.customFileMapping.controls.forEach(formGroup => {
                    if (!this.csvHeaders.includes(formGroup.value.source ?? '')) {
                        formGroup.controls.source.reset();
                    }
                });
                this.uploadedFilePreview = preview;
                return of<void>();
            })
        );
    }

    private truncatePreviewData(preview: string[][]) {
        return preview.map(row =>
            row.map(cell => (cell.length > this.maxPreviewLength ? cell.slice(0, this.maxPreviewLength) + '...' : cell))
        );
    }

    onFileRemoved() {
        this.file = undefined;
        this.playImporterForm.controls.fileName.reset();
    }

    onAddFieldMapping() {
        this.playImporterForm.controls.customFileMapping.push(new FormGroup(new FileMappingRowForm()));
        this.playImporterForm.controls.customFileMapping.markAllAsDirty();
        this.playImporterForm.markAllAsDirty();
    }

    onDeleteFileMappingRow(index: number) {
        this.playImporterForm.controls.customFileMapping.removeAt(index);
        this.playImporterForm.controls.customFileMapping.markAllAsDirty();
        this.playImporterForm.markAllAsDirty();
    }

    protected onSourceFieldSelected(value: string) {
        this.playImporterForm.controls.salesforceUniqueFieldSourceName.setValue(value);
    }

    onSave() {
        const play = {
            ...this.play(),
            updatedBy: this.loggedInUserId(),
            lastConfigured: new Date(),
        };

        if (play?.id && play?.config && this.file) {
            const uniqueFields: string[] = this.extractSalesforceUniqueFieldNames(this.playImporterForm);
            const salesforceUniqueFieldSourceName = uniqueFields[0];
            const trimmedHeaders = this.csvHeaders.map(h => h.trim());
            const toSave: Partial<PlayConfig> = {
                playFileImport: {
                    headers: trimmedHeaders,
                    fileName: this.file?.name,
                    salesforceUniqueFieldSourceName: String(salesforceUniqueFieldSourceName).trim(),

                    customFileMapping: this.transformToCustomFileMapping(
                        this.playImporterForm.controls.customFileMapping
                    ),
                },
            };

            const playId = play.id;
            const uniqueId = this.playImporterForm.controls.customFileMapping.getRawValue()[0].source;
            const mutation = firstValueFrom(
                this.#fileTransferService.validateFile(this.file.name, String(play.id), uniqueId).pipe(
                    switchMap(response => {
                        if (response.valid) {
                            play.valid = this.#playValidationStateService.determinePlayValidity(
                                'recordSelection',
                                play
                            );
                            return this.#playStore.savePlayData({ id: playId, config: toSave });
                        } else {
                            this.#snackBarService.openErrorSnackbar(
                                this.#errorHandlingService.createErrorData({
                                    messages: [response.errorMessage ?? ''],
                                    showDismissButton: true,
                                })
                            );
                            return noopFormSaveMutationResult();
                        }
                    })
                )
            );
            return mutation;
        } else {
            return noopFormSaveMutationResult();
        }
    }

    private extractSalesforceUniqueFieldNames(playFileImport: FormGroup<PlayFileImportForm>): string[] {
        const rawData = playFileImport.getRawValue();
        const customFileMapping = rawData.customFileMapping;
        return [customFileMapping[0].source, customFileMapping[0].recommendationFieldName];
    }

    private transformToCustomFileMapping(fileMappingRows: FormArray<FormGroup<FileMappingRowForm>>) {
        const customFileMapping: FileMappingRow[] = [];
        fileMappingRows
            .getRawValue()
            .slice(1) //get rid of the first row that's the unique row
            .forEach(formValue => {
                const sourceData: Omit<FileMappingRow, 'target'> = {
                    mappingRowType: 'file',
                    id: formValue.mappingId,
                    source: formValue.source,
                };
                if (formValue.recommendationFieldName) {
                    const recommendationField = this.recommendationFields().find(
                        field => field.name === formValue.recommendationFieldName
                    )!;
                    customFileMapping.push({
                        ...sourceData,
                        target: {
                            name: recommendationField.name,
                            label: recommendationField.label,
                            type: recommendationField.type,
                            objectName: this.recommendationObjectName,
                        },
                    });
                }
            });
        return customFileMapping;
    }

    private convertToSalesforceFields(metadata: SalesforceFieldMetadata[], objectName: string): SalesforceField[] {
        return metadata
            .map(data => ({
                name: data.name,
                label: data.label,
                type: data.type,
                objectName: objectName,
            }))
            .sort(function (a, b) {
                return a.label.localeCompare(b.label);
            });
    }

    private salesforceUniqueRecommendationField = computed(() => {
        const fieldName =
            this.#globalLoggedInUserStore.selectedDivisionConfig()?.config?.mapping
                ?.salesforceUniqueRecommendationFieldTargetName ?? '';
        const allRecFields = this.recommendationFields();
        const uniqueRecField = allRecFields.find(field => field.name === fieldName);
        return uniqueRecField;
    });
}
